import { useEffect, useRef, useState } from "react";
import { move } from "@dnd-kit/helpers";
import {
  DragDropProvider,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/react";
import { Plus } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { BoardCard, BoardColumn, BoardMember } from "@/features/board/types";
import { KanbanColumn } from "@/features/board/KanbanColumn";

type CardGroups = Record<string, BoardCard[]>;

type KanbanBoardProps = {
  columns: BoardColumn[];
  members: BoardMember[];
  onAddCard: (columnId: string) => void;
  onAddColumn: () => void;
  onDeleteColumn: (column: BoardColumn) => void;
  onOpenCard: (card: BoardCard) => void;
  onRenameColumn: (column: BoardColumn) => void;
  onReorder: (columns: BoardColumn[]) => Promise<void>;
};

const groupsFrom = (columns: BoardColumn[]): CardGroups =>
  Object.fromEntries(columns.map((column) => [column.id, column.cards]));

const columnsFrom = (columns: BoardColumn[], groups: CardGroups) =>
  columns.map((column) => ({
    ...column,
    cards: (groups[column.id] ?? []).map((card, position) => ({
      ...card,
      columnId: column.id,
      position,
    })),
  }));

export const KanbanBoard = ({
  columns,
  members,
  onAddCard,
  onAddColumn,
  onDeleteColumn,
  onOpenCard,
  onRenameColumn,
  onReorder,
}: KanbanBoardProps) => {
  const [groups, setGroups] = useState<CardGroups>(() => groupsFrom(columns));
  const [error, setError] = useState<string | null>(null);
  const groupsRef = useRef(groups);
  const snapshotRef = useRef(groups);

  useEffect(() => {
    const next = groupsFrom(columns);
    groupsRef.current = next;
    setGroups(next);
  }, [columns]);

  const handleDragOver = (event: DragOverEvent) => {
    if (event.operation.source?.type !== "card") return;
    setGroups((current) => {
      const next = move(current, event);
      groupsRef.current = next;
      return next;
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (event.canceled) {
      groupsRef.current = snapshotRef.current;
      setGroups(snapshotRef.current);
      return;
    }

    const nextColumns = columnsFrom(columns, groupsRef.current);
    setError(null);
    try {
      await onReorder(nextColumns);
    } catch (cause) {
      groupsRef.current = snapshotRef.current;
      setGroups(snapshotRef.current);
      setError(cause instanceof Error ? cause.message : "The new order could not be saved.");
    }
  };

  return (
    <DragDropProvider
      onDragEnd={(event) => void handleDragEnd(event)}
      onDragOver={handleDragOver}
      onDragStart={() => {
        snapshotRef.current = structuredClone(groupsRef.current);
        setError(null);
      }}
    >
      {error ? (
        <Alert className="mx-4 mb-4 border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10 sm:mx-6 lg:mx-8">
          {error}
        </Alert>
      ) : null}
      <div className="scrollbar-subtle overflow-x-auto px-4 pb-8 sm:px-6 lg:px-8">
        <div className="flex min-w-max items-start gap-3" role="list">
          {columns.map((column) => (
            <div key={column.id} role="listitem">
              <KanbanColumn
                cards={groups[column.id] ?? []}
                column={column}
                members={members}
                onAddCard={onAddCard}
                onDelete={onDeleteColumn}
                onOpenCard={onOpenCard}
                onRename={onRenameColumn}
              />
            </div>
          ))}
          <Button
            className="w-56 shrink-0 justify-start bg-white pl-2 pr-3 shadow-sm ring-1 ring-neutral-950/5"
            onClick={onAddColumn}
            type="button"
            variant="ghost"
          >
            <Plus className="size-4 shrink-0 stroke-current" />
            Add column
          </Button>
        </div>
      </div>
    </DragDropProvider>
  );
};
